import {
  SavedViewFilters,
  ArticlesViewFilters,
} from "../../../services/grantsApi";
import { getRegionLabel } from "../../../constants/regions";
import { STATUS_LABELS } from "../../../constants/grants.constants";
import { articleRoleLabels } from "../shared/articleRoleLabels";

/** Filters union covering both saved-view contexts (calls and articles). */
type AnyViewFilters = SavedViewFilters | ArticlesViewFilters;

/** Short display labels for the scheme preset, used in suggested view names. */
const SCHEME_NAME_LABELS: Record<string, string> = {
  kfs: "KFS",
  bur: "BUR",
  pozostale: "Pozostałe",
};

/**
 * Filter URL param keys that are part of SavedViewFilters (excludes page, limit).
 *
 * This list mirrors the field names used in updateUrlParams() in GrantsCalls.tsx.
 * Each entry maps a URL search param key to the corresponding SavedViewFilters field.
 */
const FILTER_PARAM_KEYS: ReadonlyArray<string> = [
  "programId",
  "competitionId",
  "status",
  "region",
  "district",
  "operatorId",
  "projectId",
  "targetGroup",
  "applicationEndDateFrom",
  "applicationEndDateTo",
  "allocationMin",
  "allocationMax",
  "labelId",
  "labelFilter",
  "search",
  "barometerOccupation",
  "barometerStatus",
  // Articles-only filter (no-op for calls views — never present in their params)
  "articleRole",
  // Preset scheme. Single-valued in both contexts (calls: kfs|bur,
  // articles: kfs|bur|pozostale). Tracked so switching presets on an active
  // saved view is correctly detected as dirty and round-trips on save.
  "scheme",
];

/**
 * Compares current URL filter params against a saved view's filters.
 * Returns true if there are differences (i.e., the view is "dirty").
 *
 * Only considers filter-related URL params (excludes page, limit).
 *
 * @param currentUrlParams - Current URL search params
 * @param savedFilters - Filters stored in the saved view
 * @returns true if current filters differ from saved view filters
 *
 * @example
 * const params = new URLSearchParams('?programId=uuid1&status=OPEN');
 * const saved = { programIds: ['uuid1'], statuses: ['OPEN'] };
 * isViewDirty(params, saved); // false (same)
 *
 * const params2 = new URLSearchParams('?programId=uuid1&status=CLOSED');
 * isViewDirty(params2, saved); // true (status differs)
 */
export function isViewDirty(
  currentUrlParams: URLSearchParams,
  savedFilters: AnyViewFilters,
): boolean {
  // Build a URLSearchParams-like representation from savedFilters for comparison
  const savedParams = filtersToParams(savedFilters);

  // Compare each filter key
  for (const key of FILTER_PARAM_KEYS) {
    const currentValues = currentUrlParams.getAll(key).sort();
    const savedValues = savedParams.getAll(key).sort();

    if (currentValues.length !== savedValues.length) return true;
    for (let i = 0; i < currentValues.length; i++) {
      if (currentValues[i] !== savedValues[i]) return true;
    }
  }

  return false;
}

/**
 * Converts saved-view filters (calls or articles) to URLSearchParams for
 * comparison. Mirrors the logic of updateUrlParams() in GrantsCalls.tsx and the
 * articles tab. Fields not present in a given context are simply skipped (no-op).
 */
function filtersToParams(filters: AnyViewFilters): URLSearchParams {
  const params = new URLSearchParams();

  // Shared fields (present on both calls and articles filters).
  filters.programIds?.forEach((id) => params.append("programId", id));
  filters.regions?.forEach((r) => params.append("region", r));
  filters.districts?.forEach((d) => params.append("district", d));
  filters.operatorIds?.forEach((id) => params.append("operatorId", id));
  filters.projectIds?.forEach((id) => params.append("projectId", id));
  filters.barometerOccupations?.forEach((o) =>
    params.append("barometerOccupation", o),
  );
  filters.barometerStatuses?.forEach((s) =>
    params.append("barometerStatus", s),
  );
  if (filters.search) {
    params.set("search", filters.search);
  }
  // Single-valued preset scheme. Works for both contexts (calls: kfs|bur,
  // articles: kfs|bur|pozostale).
  if (filters.scheme) {
    params.set("scheme", filters.scheme);
  }

  // Calls-only fields. Accessed through the calls type; absent (undefined) on
  // articles filters, so each block is a no-op for the articles context.
  const callsFilters = filters as SavedViewFilters;
  callsFilters.competitionIds?.forEach((id) =>
    params.append("competitionId", id),
  );
  callsFilters.statuses?.forEach((s) => params.append("status", s));
  callsFilters.targetGroups?.forEach((tg) => params.append("targetGroup", tg));
  if (callsFilters.applicationEndDateFrom) {
    params.set("applicationEndDateFrom", callsFilters.applicationEndDateFrom);
  }
  if (callsFilters.applicationEndDateTo) {
    params.set("applicationEndDateTo", callsFilters.applicationEndDateTo);
  }
  if (callsFilters.allocationMin !== undefined) {
    params.set("allocationMin", callsFilters.allocationMin.toString());
  }
  if (callsFilters.allocationMax !== undefined) {
    params.set("allocationMax", callsFilters.allocationMax.toString());
  }
  if (callsFilters.labelIds && callsFilters.labelIds.length > 0) {
    callsFilters.labelIds.forEach((id) => params.append("labelId", id));
  }
  if (callsFilters.labelFilter) {
    params.set("labelFilter", callsFilters.labelFilter);
  }

  // Articles-only field. Absent for calls views (no-op).
  const articlesFilters = filters as ArticlesViewFilters;
  if (articlesFilters.articleRoles && articlesFilters.articleRoles.length > 0) {
    articlesFilters.articleRoles.forEach((r) =>
      params.append("articleRole", r),
    );
  }

  return params;
}

/**
 * Builds a human-readable suggested name from active filters.
 * Used as default name when creating a new saved view.
 *
 * Logic:
 * - Takes program names, region labels, status labels
 * - Max 3-4 items, appends "..." if more
 * - Falls back to "Mój widok" if no filters are active
 *
 * @param filters - Current filter values from URL
 * @param programMap - Map of program UUID -> program name
 * @param regionMap - Map of region slug -> display label (optional, uses REGION_LABELS as default)
 * @returns Human-readable name string
 *
 * @example
 * buildSuggestedViewName(
 *   { programIds: ['uuid1'], regions: ['mazowieckie'], statuses: ['OPEN'] },
 *   { 'uuid1': 'FERS' }
 * ); // "FERS, Mazowieckie, Otwarty"
 */
export function buildSuggestedViewName(
  filters: AnyViewFilters,
  programMap: Record<string, string> = {},
  regionMap?: Record<string, string>,
): string {
  const parts: string[] = [];
  // Calls-only fields are read through this cast; undefined on articles filters.
  const callsFilters = filters as SavedViewFilters;
  // Articles-only fields are read through this cast; undefined on calls filters.
  const articlesFilters = filters as ArticlesViewFilters;

  // Programs: use name from map, fall back to ID truncated
  if (filters.programIds && filters.programIds.length > 0) {
    for (const id of filters.programIds) {
      const name = programMap[id];
      if (name) parts.push(name);
    }
  }

  // Scheme preset: include a readable label (kfs|bur|pozostale).
  if (filters.scheme) {
    parts.push(SCHEME_NAME_LABELS[filters.scheme] ?? filters.scheme);
  }

  // Regions: use display label
  if (filters.regions && filters.regions.length > 0) {
    for (const region of filters.regions) {
      const label = regionMap
        ? (regionMap[region] ?? getRegionLabel(region))
        : getRegionLabel(region);
      parts.push(label);
    }
  }

  // Statuses: use Polish label (calls only)
  if (callsFilters.statuses && callsFilters.statuses.length > 0) {
    for (const status of callsFilters.statuses) {
      const label = STATUS_LABELS[status] ?? status;
      parts.push(label);
    }
  }

  // Article roles: use Polish label (articles only)
  if (articlesFilters.articleRoles && articlesFilters.articleRoles.length > 0) {
    for (const role of articlesFilters.articleRoles) {
      parts.push(articleRoleLabels[role] ?? role);
    }
  }

  // Other active filters (districts, operators, etc.) - just count them
  const otherActiveCount = [
    filters.districts?.length ?? 0,
    callsFilters.competitionIds?.length ?? 0,
    filters.operatorIds?.length ?? 0,
    filters.projectIds?.length ?? 0,
    callsFilters.targetGroups?.length ?? 0,
    callsFilters.applicationEndDateFrom ? 1 : 0,
    callsFilters.applicationEndDateTo ? 1 : 0,
    callsFilters.allocationMin !== undefined ? 1 : 0,
    callsFilters.allocationMax !== undefined ? 1 : 0,
    callsFilters.labelIds?.length ?? 0,
    callsFilters.labelFilter ? 1 : 0,
    filters.search ? 1 : 0,
    filters.barometerOccupations?.length ?? 0,
    filters.barometerStatuses?.length ?? 0,
  ].reduce((sum, n) => sum + n, 0);

  const MAX_ITEMS = 3;

  if (parts.length === 0 && otherActiveCount === 0) {
    return "Mój widok";
  }

  if (parts.length > MAX_ITEMS) {
    return parts.slice(0, MAX_ITEMS).join(", ") + ", ...";
  }

  if (parts.length === MAX_ITEMS && otherActiveCount > 0) {
    return parts.join(", ") + ", ...";
  }

  if (parts.length > 0 && otherActiveCount > 0) {
    return parts.join(", ") + ", ...";
  }

  return parts.join(", ") || "Mój widok";
}
